import { Play, Plus, Save, Trash2, Workflow as WorkflowIcon } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { v4 as uuidv4 } from "uuid";

import { executeAppWorkflow } from "@/services/apps";
import { useBuilderStore } from "@/lib/builder/builder-store";
import type { Workflow as WorkflowType } from "@shared/types";

const defaultWorkflow: WorkflowType = {
  id: uuidv4(),
  name: "新工作流",
  trigger: { type: "manual", config: {} },
  nodes: [
    {
      id: "start",
      type: "log",
      config: { message: "工作流开始" },
      next: ["condition"],
    },
    {
      id: "condition",
      type: "condition",
      config: {
        field: "amount",
        operator: ">=",
        value: 10000,
        branches: { true: ["webhook"], false: ["log-small"] },
      },
      next: [],
    },
    {
      id: "webhook",
      type: "webhook",
      config: { url: "https://httpbin.org/post", method: "POST", body: { alert: "大额订单" } },
      next: [],
    },
    {
      id: "log-small",
      type: "log",
      config: { message: "小额订单，自动通过" },
      next: [],
    },
  ],
};

export function WorkflowPanel() {
  const { schema, setSchema } = useBuilderStore();
  const workflows = schema.workflows || [];
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [name, setName] = React.useState("");
  const [json, setJson] = React.useState("");
  const [triggerType, setTriggerType] = React.useState<WorkflowType["trigger"]["type"]>("manual");
  const [triggerConfigJson, setTriggerConfigJson] = React.useState("{}");
  const [payloadJson, setPayloadJson] = React.useState('{"amount": 15000}');
  const [result, setResult] = React.useState("");

  const selected = workflows.find((w) => w.id === selectedId);

  const updateWorkflows = (next: WorkflowType[]) => {
    setSchema({ ...schema, workflows: next });
  };

  const handleSelect = (workflow: WorkflowType) => {
    setSelectedId(workflow.id);
    setName(workflow.name);
    setJson(JSON.stringify(workflow, null, 2));
    setTriggerType(workflow.trigger.type);
    setTriggerConfigJson(JSON.stringify(workflow.trigger.config || {}, null, 2));
    setResult("");
  };

  const handleNew = () => {
    const workflow = { ...defaultWorkflow, id: uuidv4() };
    updateWorkflows([...workflows, workflow]);
    handleSelect(workflow);
  };

  const handleDelete = (id: string) => {
    updateWorkflows(workflows.filter((w) => w.id !== id));
    if (selectedId === id) {
      setSelectedId(null);
      setName("");
      setJson("");
    }
  };

  const handleSaveLocal = () => {
    try {
      const parsed = JSON.parse(json) as WorkflowType;
      const triggerConfig = JSON.parse(triggerConfigJson) as Record<string, unknown>;
      const updated: WorkflowType = {
        ...parsed,
        name,
        trigger: { type: triggerType, config: triggerConfig },
      };
      const next = workflows.map((w) => (w.id === updated.id ? updated : w));
      updateWorkflows(next);
      setResult("已保存到当前应用 Schema");
    } catch (error) {
      setResult(`JSON 解析失败：${String(error)}`);
    }
  };

  const handleExecute = async () => {
    if (!selected) return;
    try {
      const payload = JSON.parse(payloadJson) as Record<string, unknown>;
      const res = await executeAppWorkflow(schema.id, selected, payload);
      setResult(JSON.stringify(res, null, 2));
    } catch (error) {
      setResult(`执行失败：${String(error)}`);
    }
  };

  return (
    <div className="grid h-full gap-4 lg:grid-cols-[280px_1fr]">
      <Card className="h-fit">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <WorkflowIcon className="h-4 w-4" />
            工作流列表
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Button size="sm" variant="outline" className="w-full" onClick={handleNew}>
            <Plus className="mr-1.5 h-4 w-4" />
            新建
          </Button>
          {workflows.length === 0 && <p className="text-sm text-muted-foreground">暂无工作流</p>}
          {workflows.map((wf) => (
            <div
              key={wf.id}
              className={`flex cursor-pointer items-center justify-between rounded-md border p-2 text-sm hover:bg-accent ${selectedId === wf.id ? "bg-accent" : ""}`}
              onClick={() => handleSelect(wf)}
            >
              <span className="truncate">{wf.name}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(wf.id);
                }}
              >
                <Trash2 className="h-3 w-3 text-destructive" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="flex flex-col">
        <CardHeader>
          <CardTitle className="text-base">{selected ? "编辑工作流" : "新建或选择工作流"}</CardTitle>
          <CardDescription>当前使用 JSON 定义节点，支持 trigger / condition / webhook / log / updateData / sendEmail / delay / transform。</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col gap-3">
          {selected ? (
            <>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="工作流名称" />
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-xs">触发器类型</Label>
                  <Select value={triggerType} onValueChange={(v) => setTriggerType(v as WorkflowType["trigger"]["type"])}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">手动</SelectItem>
                      <SelectItem value="dataChange">数据变更</SelectItem>
                      <SelectItem value="schedule">定时调度</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">触发器配置（JSON）</Label>
                  <Textarea
                    value={triggerConfigJson}
                    onChange={(e) => setTriggerConfigJson(e.target.value)}
                    rows={3}
                    className="font-mono text-xs"
                    placeholder='{"table":"orders","events":["INSERT"]} 或 {"interval":60}'
                  />
                </div>
              </div>
              <Textarea
                value={json}
                onChange={(e) => setJson(e.target.value)}
                rows={12}
                className="flex-1 font-mono text-xs"
                placeholder="工作流节点 JSON 定义"
              />
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => void handleSaveLocal()}>
                  <Save className="mr-1.5 h-4 w-4" />
                  保存到 Schema
                </Button>
                <Button size="sm" variant="outline" onClick={() => void handleExecute()}>
                  <Play className="mr-1.5 h-4 w-4" />
                  执行
                </Button>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">执行 Payload（JSON）</label>
                <Textarea
                  value={payloadJson}
                  onChange={(e) => setPayloadJson(e.target.value)}
                  rows={3}
                  className="font-mono text-xs"
                />
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">请从左侧选择一个工作流进行编辑，或新建一个。</p>
          )}
          {result && <Textarea value={result} readOnly rows={8} className="font-mono text-xs" />}
        </CardContent>
      </Card>
    </div>
  );
}
